import { useRequiredContext, useThrower, useTrackingAsync } from "@ispb/react-hooks"
import { Fragment } from "react"
import { Badge, Table } from "react-bootstrap"
import { Else, If, Then } from "react-if"
import { useNavigate } from "react-router-dom"
import { formatDateTime } from "shared/date"
import { maybe } from "shared/maybe"
import Heading from "ui/components/Heading"
import IconButton from "ui/components/IconButton"
import IconText from "ui/components/IconText"
import Spacer from "ui/components/Spacer"
import { DEFAULT_TABLE_PROPS } from "ui/config"
import { ENERGIZED_ICON, EXECUTE_ICON, NO_RESULTS_ICON } from "ui/icons"
import { Ramda } from "../namespaces/Ramda.js"
import CollectName from "./CollectName.js"
import PublicLayout from "./Layout.js"
import StatusBadge from "./StatusBadge.js"
import { getExecutionStatus, PublicProcedureContext, useAnonymous } from "./index.js"
import { cleanExecution, EXECUTION_FRAGMENT } from "./print.js"

export default function Execute() {
    const pin = useRequiredContext(PublicProcedureContext)
    const [anonymous, setAnonymous] = useAnonymous()
    const navigate = useNavigate()
    const thrower = useThrower()
    const start = useTrackingAsync(async () => {
        try {
            const result = await pin.hasura.mutation({
                insertExecutionsOne: {
                    __args: {
                        object: {
                            companyId: pin.data.companyId,
                            procedureId: pin.data.id,
                            anonymousName: anonymous?.name,
                            anonymousCompanyName: anonymous?.companyName,
                        }
                    },
                    ...EXECUTION_FRAGMENT
                }
            })
            const resultExecution = maybe(result.insertExecutionsOne)
            pin.setData(data => {
                return {
                    ...data,
                    executions: Ramda.sortBy(_ => _.createdOn, [...data.executions, cleanExecution(resultExecution)])
                }
            })
            navigate("../execution/" + resultExecution.id)
        }
        catch (e) {
            thrower(e)
        }
    })
    const title = "Execute Procedure"
    const subtitle = (() => {
        const open = pin.data.executions.filter(execution => execution.status)
        if (open.length > 0) {
            return <Badge bg="danger"><IconText {...ENERGIZED_ICON}>{open.length} Open Executions</IconText></Badge>
        }
    })()
    const prev = (
        <Fragment>
            <Heading level="h2" title="Previous Executions" subtitle={subtitle} />
            <Table {...DEFAULT_TABLE_PROPS}>
                <thead>
                    <tr>
                        <th>Status</th>
                        <th>Started By</th>
                        <th>Started On</th>
                        <th>Last Activity By</th>
                        <th>Last Activity On</th>
                    </tr>
                </thead>
                <tbody>
                    <If condition={pin.data.executions.length > 0}>
                        <Then>
                            {
                                pin.data.executions.map(execution => {
                                    const status = getExecutionStatus(execution.actions, pin.data.controlPoints)
                                    const last = execution.actions.at(-1) ?? execution
                                    return (
                                        <tr key={execution.id}>
                                            <td><StatusBadge {...status} /></td>
                                            <td>{execution.anonymousName} @ {execution.anonymousCompanyName}</td>
                                            <td>{formatDateTime(execution.createdOn)}</td>
                                            <td>
                                                <Fragment>{last.anonymousName} @ {last.anonymousCompanyName}</Fragment>
                                            </td>
                                            <td>{maybe(execution.actions.at(-1)?.createdOn ?? execution.createdOn, formatDateTime)}</td>
                                            {/*TODO put back?
                                            <td className="text-end">
                                                <IconButton icon={EXECUTE_ICON} size="sm" onClick={() => navigate("../execution/" + execution.id)}>Resume</IconButton>
                                            </td>
                                                */}
                                        </tr>
                                    )
                                })
                            }
                        </Then>
                        <Else>
                            <tr>
                                <td colSpan={6}>
                                    <IconText {...NO_RESULTS_ICON}>This procedure has no previous executions.</IconText>
                                </td>
                            </tr>
                        </Else>
                    </If>
                </tbody>
            </Table>
        </Fragment>
    )
    if (anonymous === undefined) {
        return (
            <PublicLayout title={title}>
                <CollectName setName={setAnonymous} />
                {prev}
            </PublicLayout>
        )
    }
    //TODO show execution history
    return (
        <PublicLayout title={title}>
            <div>
                <IconButton busy={start.isRunning} onClick={start.run} icon={EXECUTE_ICON}>Start New Execution</IconButton>
                <Spacer />
                {prev}
            </div>
        </PublicLayout>
    )
}
